import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiOfficeBuildingOutline, mdiMapMarker } from '@mdi/js';
import LocationPickerModal from '../components/LocationPickerModal';
import CustomFormInput from '../components/forms/CustomFormInput';
import { authApi } from '../api/auth';
import { accountApi } from '../api/account';
import { useAuth } from '../contexts/AuthContext';
import { currencyOptions } from '../config/currencyOptions';
import { resolveToId, getDanceStyleLabel, getDanceStyleColor } from '../config/danceStyles';
import { toast } from 'react-toastify';
import './Common.css';
import '../components/AccountTypeModal.css';
import './RegisterOrganisationPage.css';

/**
 * Standalone organisation-setup page (replaces the old AccountTypeModal).
 *
 * The user is already logged in when they arrive (from RegisterPage step 1, or the
 * my-hub "Upgrade to business account" entry). Mobile never hands off here: mobile
 * signup is consumer-only and links business users to dancelocale.com/register.
 *
 * On success: createOrg, then refresh the JWT so it carries the new organisationId
 * (the page has no password to re-login with), then navigate to /dashboard.
 * Skip: the user stays a consumer (no org) and lands on /my-hub.
 */
const RegisterOrganisationPage = () => {
    const navigate = useNavigate();
    const { user, login, isInitialized } = useAuth();

    // Form state (ported from AccountTypeModal). Name comes from RegisterPage Step 1 (passed via
    // nav state) so the instructor teaching name pre-fills; falls back to the loaded account.
    const location = useLocation();
    const firstName = location.state?.firstName || user?.accountDetails?.firstName || '';
    const lastName = location.state?.lastName || user?.accountDetails?.lastName || '';

    // Two axes, deliberately separate questions.
    // Q1: does this person want to offer or sell anything at all? (null = unanswered)
    const [wantsToSell, setWantsToSell] = useState(null);
    // Q2: what do they do? Any combination, at least one required when Q1 is yes.
    const [doesClasses, setDoesClasses] = useState(false);
    const [doesPrivate, setDoesPrivate] = useState(false);
    const [doesTicketing, setDoesTicketing] = useState(false);

    // The styles the PERSON dances, answered at step 1. Used only to prefill the org-level
    // pickers below — never written back to the account from this page.
    const [personalStyles, setPersonalStyles] = useState([]);
    // ONE name. It becomes the org name, and seeds the instructor teaching name when private
    // lessons are on — a separate teaching name is editable later in Settings. Pre-filled from
    // step 1 so most people can leave it alone.
    const [orgDisplayName, setOrgDisplayName] = useState(`${firstName} ${lastName}`.trim());
    const [businessLocation, setBusinessLocation] = useState(null);
    const [baseCurrency, setBaseCurrency] = useState('AUD');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showLocationPicker, setShowLocationPicker] = useState(false);

    const needsOrg = wantsToSell === true;
    const isSchool = needsOrg && doesClasses;
    const isInstructor = needsOrg && doesPrivate;
    const isEventOrganiser = needsOrg && doesTicketing;
    // Location matters for schools/instructors (found locally) and for event organisers (their
    // business location sets the timezone their event times are interpreted and displayed in).
    // Q1 ("yes, I offer something") no longer implies Q2 has been answered — the multi-select
    // introduces a "yes, but nothing ticked yet" state the old single radio could not reach.
    // Nothing below is collectable until a tool is chosen, otherwise the form asks for required
    // fields before the question above them is answered, and ticking a box then injects
    // Teaching Name / Business Name ABOVE those fields and shoves them down the page.
    const hasChosenTool = doesClasses || doesPrivate || doesTicketing;
    const showLocation = needsOrg && hasChosenTool;
    const showBusinessFields = needsOrg && hasChosenTool;
    // Under multi-select, resolve the hero card by priority: the most operationally central
    // tool wins. Values stay inside the existing allow-list; no new values are introduced.
    const derivedPrimaryCard = () => {
        if (isSchool) return 'classes';
        if (isInstructor) return 'privateLessons';
        if (isEventOrganiser) return 'ticketing';
        return null;
    };

    // The user arrived here already logged in (RegisterPage Step 1 or the
    // my-hub upgrade entry). AuthContext only hydrates `user` once at app load, and a same-tab
    // localStorage token write (Step 1) does NOT re-notify it — so `user` can be null here even
    // though a valid token exists. Hydrate from the stored token so the protected destinations
    // (/my-hub on Skip, /dashboard on success) don't bounce to /login.
    useEffect(() => {
        if (!isInitialized) return;   // wait for AuthContext's own init to settle first
        if (user) return;             // already authenticated (e.g. from my-hub)
        const token = localStorage.getItem('accessToken');
        if (!token) {
            // Genuinely unauthenticated direct visit — not a valid entry.
            navigate('/register');
            return;
        }
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            login({
                accountId: payload.accountId,
                email: payload.email,
                organisationId: payload.organisationId,
                permissions: payload.permissions
            });
        } catch (err) {
            navigate('/register');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialized, user]);

    // Seed the personal styles used to prefill the org pickers. Two entry paths both have to
    // work: arriving from step 1 (nav state), and the my-hub upgrade entry (no nav state, so
    // read the account). Values are canonicalised to slug ids either way, mirroring Settings.
    useEffect(() => {
        const fromNav = location.state?.favouriteDanceStyles;
        if (Array.isArray(fromNav) && fromNav.length > 0) {
            setPersonalStyles([...new Set(fromNav.map(x => resolveToId(x) || x))]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const profile = await accountApi.getProfile();
                const stored = profile?.favouriteDanceStyles;
                if (!cancelled && Array.isArray(stored) && stored.length > 0) {
                    setPersonalStyles([...new Set(stored.map(x => resolveToId(x) || x))]);
                }
            } catch (err) {
                // Non-fatal: prefill is a convenience. Without it the user picks styles manually.
                console.error('Could not load personal dance styles for prefill:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [location.state]);

    // Q1. Switching axis resets the whole form so a half-filled business answer cannot survive
    // a change of mind.
    const handleSellChange = value => {
        setWantsToSell(value);
        setError('');
        setDoesClasses(false);
        setDoesPrivate(false);
        setDoesTicketing(false);
        setBusinessLocation(null);
        setBaseCurrency('AUD');
        setOrgDisplayName(`${firstName} ${lastName}`.trim());
    };

    // Skip: stay a consumer (no org) and land on my-hub.
    const handleSkip = () => {
        navigate('/my-hub');
    };

    // After org creation, mint a fresh access token carrying the new organisationId,
    // update auth context, and go to the dashboard (same destination as the old modal flow).
    const completeOrgSetup = async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            const refreshResult = await authApi.refreshAccessToken(refreshToken);
            if (refreshResult?.accessToken) {
                localStorage.setItem('accessToken', refreshResult.accessToken);
                const payload = JSON.parse(atob(refreshResult.accessToken.split('.')[1]));
                login({
                    accountId: payload.accountId,
                    email: payload.email,
                    organisationId: payload.organisationId,
                    permissions: payload.permissions
                });
            }
        } catch (err) {
            // Non-fatal: the org was created. The dashboard reconciles org context on next load.
            console.error('Token refresh after org creation failed:', err);
        }
        navigate('/dashboard');
    };

    const handleSubmit = async () => {
        // "I am just here to dance" finishes as a consumer with no organisation. Personal dance
        // styles were already saved at registration step 1, so there is nothing to write here.
        if (wantsToSell === false) {
            handleSkip();
            return;
        }

        setError('');

        if (wantsToSell === null) {
            setError('Please choose one of the two options above');
            return;
        }

        if (!doesClasses && !doesPrivate && !doesTicketing) {
            setError('Please choose at least one thing you do');
            return;
        }

        if (!orgDisplayName || orgDisplayName.trim().length < 2) {
            setError('A name is required (min 2 characters)');
            return;
        }

        if (showLocation) {
            if (!businessLocation) {
                setError('Location is required');
                return;
            }
            if (!businessLocation.latitude || !businessLocation.longitude) {
                setError('Please select a location from the autocomplete suggestions');
                return;
            }
        }

        setIsSaving(true);
        try {
            const orgName = orgDisplayName.trim();

            const orgData = {
                name: orgName,
                // Business location is collected for schools, instructors AND event organisers
                // (showLocation covers all three). For an event organiser it sets the timezone
                // their event times are displayed in, so the coordinates must be sent.
                ...(showLocation && businessLocation ? {
                    orgLocation: {
                        description: businessLocation.description,
                        latitude: businessLocation.latitude,
                        longitude: businessLocation.longitude
                    }
                } : {}),
                isDanceSchool: isSchool,
                isDanceArtist: isInstructor,
                isEventOrganiser: isEventOrganiser,
                currency: baseCurrency || 'AUD',
                // Seeded from the styles the person picked at step 1. The three fields are
                // STORED independently (the backend no longer copies one onto another), so
                // editing any one of them in Settings never touches the other two — we just
                // save everyone a third pass at the same picker during signup.
                businessDanceStyles: isSchool ? personalStyles : [],
                instructorDanceStyles: isInstructor ? personalStyles : [],
                businessBlurb: '',
                dashboardPrimaryCard: derivedPrimaryCard(),
                // Chosen at setup and written straight to settings.features, so the dashboard
                // does not ask the same question again. bulkEmail has no question of its own.
                features: {
                    classManagement: isSchool,
                    privateLessons: isInstructor,
                    ticketing: isEventOrganiser,
                    bulkEmail: true
                }
            };

            if (isInstructor) {
                orgData.teachingName = orgName;
            }

            const result = await authApi.createOrg(orgData);
            if (!result.success) {
                // Surface the upgrade guard (409 ORG_ALREADY_EXISTS) and any other failure gracefully.
                const msg = result.message || 'Failed to create your account';
                setError(msg);
                toast.error(msg);
                return;
            }

            toast.success('Your account is set up.');
            await completeOrgSetup();
        } catch (err) {
            const msg = err.message || 'Failed to create your account. Please try again.';
            setError(msg);
            toast.error(msg);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="register-org-page">
            <div className="register-org-container">
                <div className="sub-portal-header-card register-org-header">
                    <div className="register-org-header-icon">
                        <Icon path={mdiOfficeBuildingOutline} size={1.1} color="var(--dl-color-purple-default)" />
                    </div>
                    <h1 className="register-org-title">How will you use Dance Locale?</h1>
                </div>

                <div className="content-card register-org-form-card">
                    <div className="account-type-modal">
                        {error && <div className="account-type-error">{error}</div>}

                        <div className="account-type-checkboxes">
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                label="I am just here to dance"
                                checked={wantsToSell === false}
                                onChange={() => handleSellChange(false)}
                                id="rop-consumer"
                                name="wantsToSell"
                            />
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                label="I run classes, teach privates, or sell event tickets"
                                checked={wantsToSell === true}
                                onChange={() => handleSellChange(true)}
                                id="rop-seller"
                                name="wantsToSell"
                            />
                        </div>

                        {wantsToSell === false && (
                            <p className="account-type-hint">
                                No setup needed. Tap Finish setup to start exploring classes, instructors and events.
                            </p>
                        )}

                        {wantsToSell === true && (
                            <div className="account-type-field register-org-axis-divider">
                                <label className="account-type-label register-org-tools-heading">
                                    Which of these do you do?{" "}
                                    <span className="register-org-tools-heading-note">You can change this later in Settings.</span>
                                </label>
                                <p className="register-org-tools-lede">Use one tool, all three, or anything in between.</p>

                                <CustomFormInput
                                    type="checkbox"
                                    variant="purple"
                                    label="Offer private lessons"
                                    hint="Manage private lesson requests and your availability."
                                    checked={doesPrivate}
                                    onChange={e => { setDoesPrivate(e.target.checked); setError(''); }}
                                    id="rop-private"
                                />

                                <CustomFormInput
                                    type="checkbox"
                                    variant="purple"
                                    label="Run classes"
                                    hint="Manage weekly classes, terms, enrolments and attendance."
                                    checked={doesClasses}
                                    onChange={e => { setDoesClasses(e.target.checked); setError(''); }}
                                    id="rop-classes"
                                />

                                <CustomFormInput
                                    type="checkbox"
                                    variant="purple"
                                    label="Sell event tickets"
                                    hint="Sell event tickets and manage guest check-in."
                                    checked={doesTicketing}
                                    onChange={e => { setDoesTicketing(e.target.checked); setError(''); }}
                                    id="rop-ticketing"
                                />
                            </div>
                        )}

                        {showBusinessFields && (
                            <div className="account-type-business-fields">
                                {/* Every org needs these three regardless of which tools it picked:
                                    the name IS the org record, the location sets the timezone every
                                    class date and event time is interpreted in, and the currency is
                                    stamped onto every price from the first one. Styles, bio and a
                                    separate teaching name are all editable later and are driven by
                                    the setup checklist instead of this form. */}
                                <div className="account-type-field">
                                    <label className="account-type-label">Name *</label>
                                    <p className="account-type-hint">
                                        Shown to students and on your public profile. Your studio name, or your own name.
                                    </p>
                                    <input
                                        type="text"
                                        value={orgDisplayName}
                                        onChange={e => setOrgDisplayName(e.target.value)}
                                        className="account-type-input"
                                        placeholder="e.g. Rio Dance Studio, or Jane Smith"
                                    />
                                </div>

                                <div className="account-type-field">
                                    <label className="account-type-label">Base Location *</label>
                                    <p className="account-type-hint">Select your primary location</p>
                                    <button
                                        type="button"
                                        className="account-type-location-btn"
                                        onClick={() => setShowLocationPicker(true)}
                                    >
                                        <Icon path={mdiMapMarker} size={0.8} />
                                        <span>
                                            {businessLocation ? businessLocation.description : 'Select your base location'}
                                        </span>
                                    </button>
                                </div>

                                {(isSchool || isInstructor) && personalStyles.length > 0 && (
                                    <div className="account-type-field register-org-styles-carry">
                                        <p className="register-org-styles-carry-text">
                                            We will use the styles you picked for {isSchool && isInstructor
                                                ? 'your classes and your teaching profile'
                                                : isSchool ? 'your classes' : 'your teaching profile'}.
                                            You can change your dance styles anytime in Settings.
                                        </p>
                                        <div className="register-org-styles-carry-pills">
                                            {personalStyles.map(id => (
                                                <span
                                                    key={id}
                                                    className="register-org-style-pill"
                                                    style={{ background: getDanceStyleColor(id) }}
                                                >
                                                    {getDanceStyleLabel(id) || id}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="account-type-field">
                                    <label className="account-type-label">Base Currency *</label>
                                    <p className="account-type-hint">Currency for payments and invoicing.</p>
                                    <select
                                        value={baseCurrency}
                                        onChange={e => setBaseCurrency(e.target.value)}
                                        className="account-type-input"
                                    >
                                        {currencyOptions.map(curr => (
                                            <option key={curr.code} value={curr.code}>
                                                {curr.symbol} {curr.code} - {curr.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="register-org-actions">
                        <button
                            type="button"
                            className="register-org-skip-btn"
                            onClick={handleSkip}
                            disabled={isSaving}
                        >
                            Set up later
                        </button>
                        <button
                            type="button"
                            className="register-org-submit-btn"
                            onClick={handleSubmit}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Finishing...' : 'Finish setup'}
                        </button>
                    </div>
                </div>
            </div>

            <LocationPickerModal
                isOpen={showLocationPicker}
                onClose={() => setShowLocationPicker(false)}
                onSave={location => {
                    setBusinessLocation(location);
                    setShowLocationPicker(false);
                }}
                currentLocation={businessLocation}
                mode="address"
                title="Select Location"
            />
        </div>
    );
};

export default RegisterOrganisationPage;
